import {
  parseFilters,
  renderFilters,
  type FilterAst,
  type FilterClause,
  type FilterOperator,
} from './grammar';
import type { ClauseToken, FilterState } from './tokens';

/**
 * Decision D-2 of SDD-024, the console half: a report's (or custom dataset's) `filters` is the
 * PropertyFilter state — `join` and `clauses[]`, each clause an SDD-017 operator. It maps 1:1 to
 * the grammar's AST (ALL → one `and` entry per clause; ANY → one `and` entry whose `or` holds
 * every clause) and through it to the `filters` string. The service spells the same functions in
 * `services/dataservice/internal/domain/document/filters.go`; both are property-tested.
 *
 * The lake's column names carry spaces and umlauts (`prämie gesamtsumme`), which the grammar's
 * field keys do not admit, so the string form encodes every field name reversibly
 * ({@link encodeFieldKey}; SDD-024 §14.1 E-4). The editor builds its PropertyFilter catalog with
 * the same keys, so the filter widget, the URL and the service agree.
 */

export type ReportFilterJoin = 'ALL' | 'ANY';

/** One clause of a report document (`report.schema.json#/$defs/clause`). */
export interface ReportFilterClause {
  readonly field: string;
  readonly op: FilterOperator;
  readonly value?: string;
  readonly values?: readonly string[];
  /** The viewer may change the value(s) — never the field or the operator. */
  readonly editable?: boolean;
  /** The token's title in the viewer. */
  readonly label?: string;
}

export interface ReportFilters {
  readonly join: ReportFilterJoin;
  readonly clauses: readonly ReportFilterClause[];
}

export const EMPTY_REPORT_FILTERS: ReportFilters = { join: 'ALL', clauses: [] };

const NULL_OPS: readonly FilterOperator[] = ['==$null', '!=$null'];
const LIST_OPS: readonly FilterOperator[] = ['=()', '!()'];

function toAstClause(c: ReportFilterClause, field: string): FilterClause {
  if (NULL_OPS.includes(c.op)) return { field, op: c.op as '==$null' | '!=$null' };
  if (LIST_OPS.includes(c.op))
    return { field, op: c.op as '=()' | '!()', values: [...(c.values ?? [])] };
  return {
    field,
    op: c.op as Exclude<FilterOperator, '=()' | '!()' | '==$null' | '!=$null'>,
    value: c.value ?? '',
  };
}

function fromAstClause(c: FilterClause, field: string): ReportFilterClause {
  if ('values' in c) return { field, op: c.op, values: [...c.values] };
  if ('value' in c) return { field, op: c.op, value: c.value };
  return { field, op: c.op };
}

/** The document's filter as the grammar's AST (`editable` and `label` are presentation). */
export function reportFiltersToAst(
  filters: ReportFilters,
  key: (field: string) => string = (f) => f,
): FilterAst {
  const clauses = filters.clauses.map((c) => toAstClause(c, key(c.field)));
  if (clauses.length === 0) return { and: [] };
  if (filters.join === 'ANY') return { and: [{ or: clauses }] };
  return { and: clauses.map((c) => ({ or: [c] })) };
}

/**
 * The inverse of {@link reportFiltersToAst}. An AND of OR groups has no report filter form and
 * throws.
 */
export function astToReportFilters(
  ast: FilterAst,
  field: (key: string) => string = (k) => k,
): ReportFilters {
  if (ast.and.length === 0) return EMPTY_REPORT_FILTERS;
  const [first] = ast.and;
  if (ast.and.length === 1 && first !== undefined && first.or.length > 1) {
    return { join: 'ANY', clauses: first.or.map((c) => fromAstClause(c, field(c.field))) };
  }
  return {
    join: 'ALL',
    clauses: ast.and.map((group) => {
      const [only] = group.or;
      if (group.or.length !== 1 || only === undefined) {
        throw new Error('an AND of OR groups has no report filter form');
      }
      return fromAstClause(only, field(only.field));
    }),
  };
}

/** The `filters` string of a document filter, field names encoded as grammar keys. */
export function reportFiltersToGrammar(filters: ReportFilters): string {
  return renderFilters(reportFiltersToAst(filters, encodeFieldKey));
}

/** Reads a string written by {@link reportFiltersToGrammar} (or by `toFilters` over encoded keys). */
export function reportFiltersFromGrammar(raw: string): ReportFilters {
  return astToReportFilters(parseFilters(raw), decodeFieldKey);
}

const FIELD_KEY_PREFIX = 'f_';

/**
 * Any field name as a grammar field key, reversibly: ASCII letters and digits stay; every other
 * code point — the underscore included — becomes `_<hex>_`.
 * `prämie gesamtsumme` → `f_pr_e4_mie_20_gesamtsumme`.
 */
export function encodeFieldKey(name: string): string {
  let out = FIELD_KEY_PREFIX;
  for (const ch of name) {
    out += /^[A-Za-z0-9]$/.test(ch) ? ch : `_${(ch.codePointAt(0) ?? 0).toString(16)}_`;
  }
  return out;
}

/** The inverse of {@link encodeFieldKey}; throws on a key it did not write. */
export function decodeFieldKey(key: string): string {
  if (!key.startsWith(FIELD_KEY_PREFIX)) throw new Error(`"${key}" is not an encoded report field`);
  const s = key.slice(FIELD_KEY_PREFIX.length);
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch !== '_') {
      out += ch ?? '';
      continue;
    }
    const end = s.indexOf('_', i + 1);
    if (end <= i + 1) throw new Error(`"${key}" has an unterminated escape`);
    const code = Number.parseInt(s.slice(i + 1, end), 16);
    if (Number.isNaN(code)) throw new Error(`"${key}" has a bad escape`);
    out += String.fromCodePoint(code);
    i = end;
  }
  return out;
}

/** The PropertyFilter state of a document filter: one clause token per clause, keys encoded. */
export function reportFiltersToState(filters: ReportFilters): FilterState {
  return {
    join: filters.join === 'ANY' ? 'any' : 'all',
    tokens: filters.clauses.map((c): ClauseToken => ({
      kind: 'clause',
      field: encodeFieldKey(c.field),
      op: c.op,
      values: NULL_OPS.includes(c.op)
        ? []
        : LIST_OPS.includes(c.op)
          ? [...(c.values ?? [])]
          : [c.value ?? ''],
    })),
  };
}

/**
 * The document filter of a PropertyFilter state. Range tokens become their two bounds; `editable`
 * and `label` are kept from `previous` for a clause with the same field and operator. Group and raw
 * tokens (only a hand-written link has them) are dropped — the editor never authors them.
 */
export function stateToReportFilters(
  state: FilterState,
  previous: ReportFilters = EMPTY_REPORT_FILTERS,
): ReportFilters {
  const clauses: ReportFilterClause[] = [];
  for (const token of state.tokens) {
    if (token.kind === 'clause') {
      const field = decodeFieldKey(token.field);
      const base: ReportFilterClause = NULL_OPS.includes(token.op)
        ? { field, op: token.op }
        : LIST_OPS.includes(token.op)
          ? { field, op: token.op, values: [...token.values] }
          : { field, op: token.op, value: token.values[0] ?? '' };
      clauses.push(withPresentation(base, previous));
    } else if (token.kind === 'range') {
      const field = decodeFieldKey(token.field);
      if (token.from !== undefined)
        clauses.push(withPresentation({ field, op: '>=', value: token.from }, previous));
      if (token.to !== undefined)
        clauses.push(withPresentation({ field, op: '<=', value: token.to }, previous));
    }
  }
  return { join: state.join === 'any' ? 'ANY' : 'ALL', clauses };
}

function withPresentation(clause: ReportFilterClause, previous: ReportFilters): ReportFilterClause {
  const match = previous.clauses.find((c) => c.field === clause.field && c.op === clause.op);
  if (match === undefined) return clause;
  return {
    ...clause,
    ...(match.editable === true ? { editable: true } : {}),
    ...(match.label !== undefined ? { label: match.label } : {}),
  };
}

/** A viewer's value for an editable clause (the wire's `FilterValue`). */
export interface ReportFilterValue {
  readonly index: number;
  readonly values: readonly string[];
}

const PARAM = /^f\.(\d+)$/;

/**
 * The viewer's editable filter values from the URL search: `f.<index>=<value>` (D-2, U-4). A list
 * operator's values travel `|`-separated with the grammar's escape (`\|`).
 */
export function filterValuesFromSearch(
  search: Readonly<Record<string, unknown>>,
  filters: ReportFilters,
): ReportFilterValue[] {
  const out: ReportFilterValue[] = [];
  for (const [name, raw] of Object.entries(search)) {
    const m = PARAM.exec(name);
    if (m === null || typeof raw !== 'string') continue;
    const index = Number(m[1]);
    const clause = filters.clauses[index];
    if (clause?.editable !== true) continue;
    out.push({ index, values: LIST_OPS.includes(clause.op) ? splitList(raw) : [raw] });
  }
  return out.sort((a, b) => a.index - b.index);
}

/** The URL search entries of the viewer's values — the inverse of {@link filterValuesFromSearch}. */
export function filterValuesToSearch(
  values: readonly ReportFilterValue[],
  filters: ReportFilters,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const v of values) {
    const clause = filters.clauses[v.index];
    if (clause?.editable !== true) continue;
    out[`f.${String(v.index)}`] = LIST_OPS.includes(clause.op)
      ? v.values.map((x) => x.replace(/[\\|]/g, (c) => `\\${c}`)).join('|')
      : (v.values[0] ?? '');
  }
  return out;
}

function splitList(raw: string): string[] {
  const parts: string[] = [];
  let current = '';
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (ch === '\\' && i + 1 < raw.length) {
      current += raw[i + 1] ?? '';
      i++;
    } else if (ch === '|') {
      parts.push(current);
      current = '';
    } else current += ch ?? '';
  }
  parts.push(current);
  return parts;
}

/** The document filter with the viewer's values applied (the saved clause unless a value is supplied). */
export function applyFilterValues(
  filters: ReportFilters,
  values: readonly ReportFilterValue[],
): ReportFilters {
  const clauses = [...filters.clauses];
  for (const v of values) {
    const c = clauses[v.index];
    if (c?.editable !== true) continue;
    clauses[v.index] = LIST_OPS.includes(c.op)
      ? { ...c, values: [...v.values] }
      : { ...c, value: v.values[0] ?? '' };
  }
  return { join: filters.join, clauses };
}
